import React, { useRef, useState, useEffect } from 'react';
import styles from './SharePopover.module.css';
import classnames from 'classnames';
import { ApplicationView } from '@aha-app/mvc';
import type {
  AssistantChat,
  AssistantArtifact,
} from 'components/framework/models';

interface SharePopoverProps {
  shareUrl: string;
  name: string;
  iconOnly?: boolean;
  tokenOwner: AssistantChat | AssistantArtifact;
  buttonKind?: 'plain' | 'icon';
}

const SharePopover: React.FC<SharePopoverProps> = ({
  shareUrl,
  name,
  tokenOwner,
  buttonKind = 'plain',
  iconOnly = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!isVisible) return;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isVisible]);

  const handleRegenerateLink = () => {
    setIsRegenerating(true);
    void tokenOwner.regenerateToken().finally(() => {
      setIsRegenerating(false);
    });
  };

  const togglePopover = () => {
    if (!popoverRef.current) return;

    if (isVisible) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <aha-button kind={buttonKind} size='mini' onClick={togglePopover}>
        <i className='far fa-arrow-up-from-bracket' />
        {iconOnly ? null : ' Share'}
      </aha-button>
      <aha-tooltip>Share this {name}</aha-tooltip>

      <div
        ref={popoverRef}
        className={classnames(styles.popoverContent, {
          [styles.hidden]: !isVisible,
        })}
      >
        <div className={styles.linkContainer}>
          <a
            className={styles.link}
            href={isRegenerating ? '#' : `${window.location.origin}${shareUrl}`}
            target='_blank'
            rel='noopener noreferrer'
            onClick={e => e.stopPropagation()}
          >
            {isRegenerating ? (
              <span>Generating new link...</span>
            ) : (
              <>
                <span>{`${window.location.origin}${shareUrl}`}</span>
                <i className='fa-solid fa-arrow-up-right-from-square' />
              </>
            )}
          </a>
        </div>
        <div className={styles.footer}>
          <aha-button
            kind='default'
            size='small'
            onClick={handleRegenerateLink}
            {...(isRegenerating ? { disabled: true } : {})}
          >
            {isRegenerating ? 'Generating...' : 'Generate new link'}
          </aha-button>
          <aha-copy-button
            kind='primary'
            size='small'
            text={`${window.location.origin}${shareUrl}`}
          />
          <aha-tooltip>Copy to clipboard</aha-tooltip>
        </div>
      </div>
    </div>
  );
};

export default ApplicationView(SharePopover);
